import { useTranslation } from "react-i18next";
import React, { useEffect, useState, useMemo } from 'react';
import {
	Button,
	Checkbox,
	Input,
	Pagination,
	Select,
	Space,
	Table,
	Typography,
	Tooltip,
} from 'antd';
import { DeleteOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import AddUserPermissionModal from './AddUserPermissionModal';
import FormItemDropdown from '../form-dropdown/FormItemDropdown';
import { permissionOptions } from '../../utils/sharePermission';
import { getAllTeacherAndFacility } from '../../service/library/question-list';
import { AuthorizedUserType } from '../../types/listTrainingNeeds';
import { globalStore } from '../../stores/stores';
import TableData from '../table-data/TableData';

const { Option } = Select;
const { Search } = Input;

interface ShareUser {
	id: number;
	name: string;
	email: string;
	permission: string;
	isAuthor?: boolean;
}

interface Props {
	authorizedLecturerList: {
		userId: number;
		permission: string;
	}[];
	onDataChange?: (data: any[]) => void;
}

const PERMISSIONS = {
	AUTHOR: '1',
	VIEW: '2',
	EDIT: '3',
	REMOVE: '4',
};

const SharePermissionPersonalTab = (props: Props) => {
  const { t } = useTranslation();
	const { authorizedLecturerList, onDataChange } = props;
	const [teacherList, setTeacherList] = useState<AuthorizedUserType[]>([]);
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [keyword, setKeyword] = useState('');
	const [searchKeyword, setSearchKeyword] = useState('');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [authorizedUsers, setAuthorizedUsers] = useState<ShareUser[]>([]);
	const { personalInfo } = globalStore();
	const [isMine, setIsMine] = useState(false);

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);

	useEffect(() => {
		(async () => {
			const res = await getAllTeacherAndFacility({});
			const uniq = new Map();
			res?.data?.data?.content?.forEach((u: AuthorizedUserType) =>
				u.email && !uniq.has(u.email) && uniq.set(u.email, u));
			setTeacherList(Array.from(uniq.values()));
		})();
	}, []);

	useEffect(() => {
		if (!teacherList.length || !personalInfo?.email) return;

		if (!authorizedLecturerList || authorizedLecturerList.length === 0) {
			const currentUser = teacherList.find(t => t.email === personalInfo.email);
			if (currentUser) {
				setAuthorizedUsers([{
					id: currentUser.id,
					name: currentUser.name || personalInfo.fullName,
					email: currentUser.email,
					permission: PERMISSIONS.AUTHOR,
					isAuthor: true
				}]);
				setIsMine(true);
			}
		} else {
			const mappedUsers = authorizedLecturerList.map(lecturer => {
				const teacher = teacherList.find(t => t.id === lecturer.userId);
				const isCurrentUserAuthor = lecturer.permission === PERMISSIONS.AUTHOR &&
					teacher?.email === personalInfo.email;

				if (isCurrentUserAuthor) {
					setIsMine(true);
				}

				return {
					id: lecturer.userId,
					name: teacher?.name || '',
					email: teacher?.email || '',
					permission: lecturer.permission.toString(),
					isAuthor: lecturer.permission.toString() === PERMISSIONS.AUTHOR
				};
			});
			setAuthorizedUsers(mappedUsers);

			const hasAuthor = mappedUsers.some(u => u.permission === PERMISSIONS.AUTHOR);
			if (!hasAuthor) {
				setIsMine(false);
			}
		}
	}, [teacherList, authorizedLecturerList, personalInfo]);

	useEffect(() => {
		if (onDataChange) {
			onDataChange(authorizedUsers);
		}
	}, [authorizedUsers, onDataChange]);

	const filteredData = useMemo(() => {
		if (!searchKeyword) return authorizedUsers;

		return authorizedUsers.filter((item) =>
			item.email.toLowerCase().includes(searchKeyword.toLowerCase()) ||
			item.name.toLowerCase().includes(searchKeyword.toLowerCase())
		);
	}, [authorizedUsers, searchKeyword]);

	const paginatedData = useMemo(() => {
		const startIndex = (currentPage - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		return filteredData.slice(startIndex, endIndex);
	}, [filteredData, currentPage, pageSize]);

	useEffect(() => {
		setCurrentPage(1);
	}, [searchKeyword]);

	const handleSearch = () => {
		setSearchKeyword(keyword);
	};

	const handleAddUsers = (
		users: { email: string; name: string; permission: string }[]
	) => {
		const newRows = users.map((u) => {
			const teacher = teacherList.find(t => t.email === u.email);
			return {
				id: teacher?.id || Date.now() + Math.random(),
				name: teacher?.name || u.name,
				email: u.email,
				permission: u.permission === PERMISSIONS.AUTHOR ? PERMISSIONS.VIEW : u.permission,
				isAuthor: false
			};
		});
		setAuthorizedUsers((prev) => [...prev, ...newRows]);
		setIsModalOpen(false);
	};

	const handleDelete = (id: number) => {
		const user = authorizedUsers.find(item => item.id === id);
		if (user?.permission === PERMISSIONS.AUTHOR) {
			return;
		}
		setAuthorizedUsers((prev) => prev.filter((item) => item.id !== id));
	};

	const handlePermissionChange = (value: string, userId: number) => {
		if (value === PERMISSIONS.AUTHOR) {
			const updated = authorizedUsers.map((user) => {
				if (user.id === userId) {
					return { ...user, permission: PERMISSIONS.AUTHOR, isAuthor: true };
				} else if (user.permission === PERMISSIONS.AUTHOR) {
					return { ...user, permission: PERMISSIONS.EDIT, isAuthor: false };
				}
				return user;
			});
			setAuthorizedUsers(updated);
		} else if (value === PERMISSIONS.REMOVE) {
			handleDelete(userId);
		} else {
			setAuthorizedUsers((prev) =>
				prev.map((item) =>
					item.id === userId ? { ...item, permission: value } : item
				)
			);
		}
	};

	const getPermissionOptions = (record: ShareUser) => {
		if (record.permission === PERMISSIONS.AUTHOR) {
			return [];
		}

		if (isMine) {
			return permissionOptions;
		} else {
			return permissionOptions.filter(opt => opt.id === PERMISSIONS.VIEW || opt.id === PERMISSIONS.EDIT);
		}
	};

	const columns = [
		{
			title: 'STT',
			key: 'stt',
			width: 60,
			render: (_: any, __: any, index: number) => (
				<span>{(currentPage - 1) * pageSize + index + 1}</span>
			),
		},
		{
			title: t("Giảng viên", "Giảng viên"),
			dataIndex: 'name',
			key: 'name',
			render: (_: any, record: ShareUser) => (
				<div>
					<Typography.Text strong>{record.name}</Typography.Text>
					<br />
					<Typography.Text type="secondary">{record.email}</Typography.Text>
				</div>
			),
		},
		{
			title: t("Quyền", "Quyền"),
			dataIndex: 'permission',
			key: 'permission',
			width: 200,
			render: (_: any, record: ShareUser) => {
				if (record.permission === PERMISSIONS.AUTHOR) {
					return <Typography.Text italic>{t("Tác giả", "Tác giả")}</Typography.Text>;
				}

				const options = getPermissionOptions(record);

				return (
					<FormItemDropdown
						description
						value={record.permission}
						onChange={(value) => handlePermissionChange(value, record.id)}
						options={options}
						style={{ width: "180px" }}
						disabled={options.length === 0}
					/>
				);
			},
		},
		{
			title: '',
			key: 'action',
			width: 48,
			render: (_: any, record: ShareUser) => {
				const canDelete = isMine && record.permission !== PERMISSIONS.AUTHOR;

				return canDelete ? (
					<Tooltip title={t("Xóa quyền truy cập", "Xóa quyền truy cập")}>
						<Button
							icon={<DeleteOutlined />}
							danger
							type="text"
							onClick={() => handleDelete(record.id)}
						/>
					</Tooltip>
				) : null;
			},
		},
	];

	const handleTableChange = (pagination: any, filters: any, sorter: any, extra: any) => {
		console.log('Table change:', { pagination, filters, sorter, extra });
	};

	const handleBulkDelete = () => {
		const idsToDelete = selectedRowKeys.filter(key => {
			const user = authorizedUsers.find(u => u.id === key);
			return user?.permission !== PERMISSIONS.AUTHOR;
		});
		setAuthorizedUsers(prev => prev.filter(item => !idsToDelete.includes(item.id)));
		setSelectedRowKeys([]);
	};

	const handleRowSelection = (rows: ShareUser[]) => {
		const filteredRows = rows.filter(row => row.permission !== PERMISSIONS.AUTHOR);
		setSelectedRowKeys(filteredRows.map(row => row.id));
	};

	return (
		<>
			<Space style={{ marginBottom: 16, justifyContent: 'space-between', width: '100%' }}>
				<Space>
					<Button
						icon={<PlusOutlined />}
						onClick={() => setIsModalOpen(true)}
						disabled={!isMine}
					>
						{t("Thêm", "Thêm")}
					</Button>
					{selectedRowKeys.length > 0 && isMine && (
						<Button
							danger
							onClick={handleBulkDelete}
						>
							{t("Xóa", "Xóa")} {selectedRowKeys.length} {t("người dùng", "người dùng")}
						</Button>
					)}
				</Space>
				<Search
					placeholder={t("Tìm kiếm theo email hoặc tên", "Tìm kiếm theo email hoặc tên")}
					value={keyword}
					onChange={(e) => setKeyword(e.target.value)}
					onSearch={handleSearch}
					onPressEnter={handleSearch}
					style={{ width: 300 }}
					allowClear
					enterButton={
						<Button
							icon={<SearchOutlined />}
						>
						</Button>
					}
				/>
			</Space>

			<TableData
				dataTable={paginatedData}
				dataColumns={columns}
				totalRecords={filteredData.length}
				pageNumber={currentPage}
				pageSize={pageSize}
				pagination={true}
				setPageNumber={setCurrentPage}
				setPageSize={setPageSize}
				hideSelection={false}
				setSelectedRowKeysProps={handleRowSelection}
				rowSelected={selectedRowKeys.map(key => ({ id: key }))}
				handleTableChangeProps={handleTableChange}
				unit={t("giảng viên", "giảng viên")}
				searchValue={searchKeyword}
				disableRowCheckbox={(record: ShareUser) => record.permission === PERMISSIONS.AUTHOR}
				localeProp={{
					emptyText: searchKeyword
						? `Không tìm thấy kết quả với từ khóa "${searchKeyword}"`
						: t("Chưa có giảng viên nào được chia sẻ", "Chưa có giảng viên nào được chia sẻ")
				}}
				scroll={{ x: 1000, y: 540 }}
			/>

			<AddUserPermissionModal
				open={isModalOpen}
				onCancel={() => setIsModalOpen(false)}
				onOk={handleAddUsers}
				authorizedUsers={authorizedUsers}
			/>
		</>
	);
};

export default SharePermissionPersonalTab;